import type { ProductionRecord } from '../types/production'

import { api } from './api'
import { normalizeToIsoZMidnight } from './dateNormalization'
import { resolveCanonicalLotKey } from './lotIdentity'
import { deleteScopedKey, readWaterJSON, writeWaterJSON } from './waterStorage'
import { deleteKey, listStorageKeys } from './storage'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow, syncEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { dexieActivityRepo } from '../offline/repos/DexieActivityRepo'
import { getActiveTenantKey } from '../offline/tenant'
import { ensureOfflineReady } from '../offline/initOffline'
import { getOfflineDb } from '../offline/dbFactory'
import { dexieProductionTableRepo } from '../offline/repos/DexieProductionTableRepo'
import {
  buildQueuedEntityMutationResult,
  clearEntityBatchSyncQueueBlockers,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatus,
  type EntityBatchSyncStatus,
  type EntityMutationResult,
  type EntityMutationSyncState
} from './entityMutationSync'

let pendingOutboxSyncTimeout: ReturnType<typeof setTimeout> | null = null

const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine

const requestDebouncedOutboxSync = (maxEntries: number): void => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  if (pendingOutboxSyncTimeout) {
    clearTimeout(pendingOutboxSyncTimeout)
  }
  pendingOutboxSyncTimeout = setTimeout(() => {
    pendingOutboxSyncTimeout = null
    void (syncEntityOutboxNow({ maxEntries }).catch(() => {}))
  }, 1200)
}

export type BiomassDataRow = {
  id: string
  date: string
  lot: string
  lotId?: string
  unmappedLot?: string
  stage: 'Active' | 'Inactive'
  weight: string
  dailyMortality: string
  fishStock: string
  health: string
  healthDetail: string
  [key: string]: string | undefined
  archived?: '1'
}

/**
 * v2.2 T3-D-04b: archivability is now decided by the LOT'S own lifecycle
 * stage on the Lot identity record, not by aggregating snapshot values
 * on production rows. `Lot.stage` is the Tier F source of truth for
 * lifecycle (see docs/architecture-concerns.md invariant I-1); rewriting
 * the row-aggregate signature away closes a stale-cache class of bugs
 * where a lot's rows could disagree with the current lot state.
 *
 * A lot is archivable when its stage is `'Inactive'` or `'Harvested'` —
 * the two terminal states in the 5-value `LotStage` enum. `'Active'`,
 * `'Planned'`, and `'Dormant'` lots stay in operational lists.
 */
export const isLotArchivable = (lot: { stage?: unknown } | null | undefined): boolean => {
  if (!lot || typeof lot !== 'object') return false
  if (typeof lot.stage !== 'string') return false
  const stage = lot.stage.trim()
  return stage === 'Inactive' || stage === 'Harvested'
}

export type ProductionUnitTableContext = {
  farmId: string
  productionUnitId: string
  farmName?: string
  productionUnitName?: string
}

export type ProductionUnitTableRowMutationResult = EntityMutationResult<BiomassDataRow>
export type ProductionUnitTableRowDeleteResult = EntityMutationResult<void>

const LEGACY_BLOB_KEY = 'productionUnitTableData'
const RETIRED_SURVIVAL_FIELD_KEYS = new Set(['survival', 'survivalrate', 'survivalpct', 'survivalpercentage'])

const normalizeRetiredFieldKey = (value: string): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

export const isRetiredSurvivalFieldKey = (value: string): boolean =>
  RETIRED_SURVIVAL_FIELD_KEYS.has(normalizeRetiredFieldKey(value))

export const stripRetiredSurvivalColumns = (columns: string[]): string[] =>
  columns.filter((column) => !isRetiredSurvivalFieldKey(column))

const canonicalKey = (context: ProductionUnitTableContext): string => (
  `production-unit-table-v2-${context.farmId}-${context.productionUnitId}`
)

const nameKey = (context: ProductionUnitTableContext): string | null => {
  const farmName = (context.farmName ?? '').trim()
  const unitName = (context.productionUnitName ?? '').trim()
  if (!farmName || !unitName) return null
  return `${farmName}-${unitName}`
}

const idKey = (context: ProductionUnitTableContext): string => `${context.farmId}-${context.productionUnitId}`

const stripRetiredSurvivalFieldsFromRow = (row: BiomassDataRow): BiomassDataRow => {
  let nextRow: BiomassDataRow | null = null

  Object.keys(row).forEach((key) => {
    if (!isRetiredSurvivalFieldKey(key)) return
    nextRow ??= { ...row }
    delete (nextRow as Record<string, unknown>)[key]
  })

  return nextRow ?? row
}

const stripRetiredSurvivalFields = (rows: BiomassDataRow[]): BiomassDataRow[] => {
  let changed = false

  const next = rows.map((row) => {
    const stripped = stripRetiredSurvivalFieldsFromRow(row)
    if (stripped !== row) changed = true
    return stripped
  })

  return changed ? next : rows
}

const ensureDailyMortalityField = (rows: BiomassDataRow[]): BiomassDataRow[] => {
  let changed = false

  const next = rows.map((row) => {
    if (row.dailyMortality !== undefined) return row
    changed = true
    return { ...row, dailyMortality: '' }
  })

  return changed ? next : rows
}

const sanitizeStoredRows = (rows: BiomassDataRow[]): BiomassDataRow[] =>
  dedupeRowsById(ensureDailyMortalityField(stripRetiredSurvivalFields(rows)))

const dedupeRowsById = (rows: BiomassDataRow[]): BiomassDataRow[] => {
  if (rows.length <= 1) return rows
  const seen = new Set<string>()
  let changed = false
  const out: BiomassDataRow[] = []

  for (const row of rows) {
    const id = String(row?.id ?? '').trim()
    if (!id) {
      out.push(row)
      continue
    }
    if (seen.has(id)) {
      changed = true
      continue
    }
    seen.add(id)
    out.push(row)
  }

  return changed ? out : rows
}

const migrateExistingDates = (rows: BiomassDataRow[]): BiomassDataRow[] => {
  let migrated = false

  const next = rows.map((row) => {
    const originalDate = String(row.date ?? '').trim()
    if (!originalDate) return row

    const normalized = normalizeToIsoZMidnight(originalDate)
    if (!normalized.ok) return row
    if (normalized.isoZ === originalDate) return row

    migrated = true
    return { ...row, date: normalized.isoZ }
  })

  return migrated ? next : rows
}

const mergeRowsById = (preferred: BiomassDataRow[], secondary: BiomassDataRow[]): BiomassDataRow[] => {
  if (preferred.length === 0) return secondary
  if (secondary.length === 0) return preferred
  const byId = new Set(preferred.map((row) => row.id))
  const merged = preferred.slice()
  for (const row of secondary) {
    if (!byId.has(row.id)) merged.push(row)
  }
  return merged
}

const toRowFromRecord = (record: ProductionRecord): BiomassDataRow => {
  const measurements = record.measurements && typeof record.measurements === 'object'
    ? record.measurements
    : {}

  // T3-A-07b (2026-07-06): the ProductionRecord biology cache columns
  // (`weightGrams`, `dailyMortality`, `fishStock`) are retired end-to-end.
  // The client-shape row's biology fields are always seeded empty here;
  // the overlay chain (`overlayWeightSamplesOnProductionRows` +
  // `overlayMortalityLogsOnProductionRows` + `overlayFishStockOnProductionRows`)
  // fills them in at render time from the WSL / MortalityLog / FishStockLog
  // event streams. See [docs/architecture-concerns.md](../../docs/architecture-concerns.md)
  // invariant I-1 (Tier A event streams authoritative over Tier B cache).
  const base: BiomassDataRow = {
    id: record.id,
    date: record.recordDate,
    lot: record.lot ?? '',
    lotId: record.lotId,
    stage: record.stage === 'Inactive' ? 'Inactive' : 'Active',
    weight: '',
    dailyMortality: '',
    fishStock: '',
    health: typeof record.health === 'string' ? record.health : '',
    healthDetail: typeof record.healthDetail === 'string' ? record.healthDetail : ''
  }

  Object.entries(measurements).forEach(([key, value]) => {
    if (key in base) return
    if (isRetiredSurvivalFieldKey(key)) return
    if (value === null || value === undefined) {
      base[key] = ''
      return
    }
    if (typeof value === 'string') {
      base[key] = value
      return
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      base[key] = String(value)
      return
    }
    base[key] = JSON.stringify(value)
  })

  return base
}

const toRecordPayload = (context: ProductionUnitTableContext, row: BiomassDataRow): Record<string, unknown> => {
  const known = new Set([
    'id',
    'date',
    'lot',
    'lotId',
    'unmappedLot',
    'stage',
    'weight',
    'dailyMortality',
    'fishStock',
    'health',
    'healthDetail',
    'archived'
  ])

  const measurements: Record<string, unknown> = {}
  Object.keys(row).forEach((key) => {
    if (known.has(key)) return
    if (isRetiredSurvivalFieldKey(key)) return
    measurements[key] = row[key]
  })

  // T3-A-07b (2026-07-06): the ProductionRecord biology cache columns are
  // retired; do NOT emit `weightGrams` / `dailyMortality` / `fishStock` on
  // the outbound Prisma payload. The event streams (`WeightSampleLog`,
  // `MortalityLog`, `FishStockLog`) are authoritative. `weight` /
  // `dailyMortality` / `fishStock` cells on the client-shape row are
  // ignored here — they remain in local state only until the overlay
  // chain replaces them at render time.
  return {
    id: row.id,
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    lotId: row.lotId,
    lot: row.lot,
    recordDate: row.date,
    stage: row.stage,
    health: row.health,
    healthDetail: row.healthDetail,
    measurements: Object.keys(measurements).length > 0 ? measurements : undefined
  }
}

const readLegacyBlob = (): Record<string, BiomassDataRow[]> =>
  readWaterJSON<Record<string, BiomassDataRow[]>>(LEGACY_BLOB_KEY, {})

const writeLegacyBlob = (blob: Record<string, BiomassDataRow[]>): void =>
  writeWaterJSON(LEGACY_BLOB_KEY, blob)

const isValidIsoDate = (value: string): boolean => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return false
  const parsed = new Date(trimmed)
  return !Number.isNaN(parsed.getTime())
}

const resolveProductionLotToken = (value: string | null | undefined): string | null => {
  const token = resolveCanonicalLotKey(value)
  return token === 'unknown-lot' ? null : token
}

const resolveProductionRowLotToken = (row: Pick<BiomassDataRow, 'lot' | 'lotId'>): string | null => {
  const lotIdToken = resolveProductionLotToken(row.lotId)
  if (lotIdToken) {
    return lotIdToken
  }

  const visibleLotToken = resolveProductionLotToken(row.lot)
  if (visibleLotToken) {
    return visibleLotToken
  }

  return null
}

export const isSyncableProductionRow = (row: BiomassDataRow): boolean => {
  const lot = String(row.lot ?? '').trim()
  if (!lot) return false

  const stage = row.stage
  if (stage !== 'Active' && stage !== 'Inactive') return false

  const recordDate = String(row.date ?? '').trim()
  if (recordDate && !isValidIsoDate(recordDate)) return false

  return true
}

export const loadProductionUnitTableLegacyBlob = <T = unknown>(): Record<string, T> =>
  readWaterJSON<Record<string, T>>(LEGACY_BLOB_KEY, {} as Record<string, T>)

export const saveProductionUnitTableLegacyBlob = <T = unknown>(blob: Record<string, T>): void => {
  writeWaterJSON(LEGACY_BLOB_KEY, blob)
}

export const backupAndRewriteProductionUnitTableLegacyBlob = <T = unknown>(args: {
  backupKey: string
  originalData: Record<string, T>
  updatedData: Record<string, T>
}): void => {
  writeWaterJSON(args.backupKey, args.originalData)
  writeWaterJSON(LEGACY_BLOB_KEY, args.updatedData)

  // Prevent localStorage bloat: keep only a small number of recent backups.
  // Backups store the entire legacy blob, so they can quickly exceed quota.
  try {
    const tenantKey = getActiveTenantKey()
    if (typeof window === 'undefined') return
    const prefix = 'productionUnitTableData-backup-'
    const scopedSuffix = `::${tenantKey}`
    const candidates: Array<{ key: string; ts: number }> = []

    for (const key of listStorageKeys()) {
      if (!key) continue
      if (!key.startsWith(prefix)) continue
      if (!key.endsWith(scopedSuffix) && !(tenantKey === 'default' && !key.includes('::'))) continue

      const base = key.split('::')[0] ?? key
      const rawTs = base.slice(prefix.length)
      const ts = Number(rawTs)
      candidates.push({ key, ts: Number.isFinite(ts) ? ts : 0 })
    }

    candidates.sort((a, b) => b.ts - a.ts)
    const KEEP = 2
    candidates.slice(KEEP).forEach(({ key }) => {
      try {
        deleteKey(key)
      } catch {
        // ignore
      }
    })
  } catch {
    // ignore best-effort cleanup
  }
}

export const deleteProductionUnitTableDataset = (context: ProductionUnitTableContext): void => {
  deleteScopedKey(canonicalKey(context))

  const blob = readLegacyBlob()
  const byName = nameKey(context)
  if (byName) delete blob[byName]
  delete blob[idKey(context)]
  writeLegacyBlob(blob)

  // Best-effort: also delete Dexie-backed rows.
  void (async () => {
    try {
      await ensureOfflineReady()
      // Replace with empty set to clear all rows for this context.
      await dexieProductionTableRepo.replaceAllForContext(context, [])
    } catch {
      // ignore
    }
  })()
}

export const loadAllProductionUnitTableRows = (): BiomassDataRow[] => {
  const blob = readLegacyBlob()
  const all: BiomassDataRow[] = []
  Object.values(blob).forEach((rows) => {
    if (Array.isArray(rows)) all.push(...rows)
  })
  return sanitizeStoredRows(all)
}

export const loadProductionUnitTableRows = (context: ProductionUnitTableContext): BiomassDataRow[] => {
  const blob = readLegacyBlob()
  const rowsById = blob[idKey(context)] ?? []
  const rowsByName = (() => {
    const key = nameKey(context)
    return key ? (blob[key] ?? []) : []
  })()

  // IMPORTANT: Prefer stable ID-keyed buckets.
  // Name-keyed buckets are legacy and can become stale (renames) or collide (same names),
  // which can leak rows across contexts if we treat them as canonical.
  const merged = rowsById.length > 0
    ? rowsById
    : rowsByName

  const upgraded = sanitizeStoredRows(merged)
  const migrated = migrateExistingDates(upgraded)
  if (migrated !== upgraded || upgraded !== merged) {
    // Persist migration back to both canonical dataset and legacy blob for consistency.
    saveProductionUnitTableRows(context, migrated, { skipSync: true })
    return migrated
  }

  writeWaterJSON(canonicalKey(context), upgraded)
  return upgraded
}

// Dexie-backed storage (preferred for large offline datasets).

const migrateContextFromLegacyIfNeeded = async (context: ProductionUnitTableContext): Promise<void> => {
  await ensureOfflineReady()
  const existing = await dexieProductionTableRepo.countByContext(context)
  if (existing > 0) return

  const legacyRows = loadProductionUnitTableRows(context)
  if (legacyRows.length === 0) return

  await dexieProductionTableRepo.upsertMany(context, legacyRows)
  void dexieActivityRepo.log({
    source: 'local',
    level: 'info',
    title: 'Production table migrated',
    detail: `Migrated ${legacyRows.length} row${legacyRows.length === 1 ? '' : 's'} from legacy storage into offline DB`,
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    dedupeKey: `productionTable:migrate:${context.farmId}:${context.productionUnitId}`,
    dedupeWindowMs: 86_400_000
  }).catch(() => {})
}

export const listProductionUnitTableRows = async (context: ProductionUnitTableContext): Promise<BiomassDataRow[]> => {
  await migrateContextFromLegacyIfNeeded(context)
  const rows = await dexieProductionTableRepo.listByContext(context)
  const sanitized = sanitizeStoredRows(rows)
  if (sanitized !== rows) {
    await dexieProductionTableRepo.replaceAllForContext(context, sanitized)
  }
  return sanitized.filter((row) => row.archived !== '1')
}

export const listProductionUnitTableRowsIncludingArchived = async (
  context: ProductionUnitTableContext
): Promise<BiomassDataRow[]> => {
  await migrateContextFromLegacyIfNeeded(context)
  const rows = await dexieProductionTableRepo.listByContext(context)
  const sanitized = sanitizeStoredRows(rows)
  if (sanitized !== rows) {
    await dexieProductionTableRepo.replaceAllForContext(context, sanitized)
  }
  return sanitized
}

export const archiveProductionUnitTableRowsForLot = async (args: {
  context: ProductionUnitTableContext
  lot: string
  rowIds?: string[]
}): Promise<number> => {
  const { context, lot } = args
  const lotToken = resolveProductionLotToken(lot)
  if (!lotToken) return 0

  const allRows = await listProductionUnitTableRowsIncludingArchived(context)
  const rowIdSet = Array.isArray(args.rowIds) && args.rowIds.length > 0
    ? new Set(args.rowIds.map((id) => String(id)))
    : null

  const toArchive = allRows.filter((row) => {
    if (!row || row.archived === '1') return false
    if (resolveProductionRowLotToken(row) !== lotToken) return false
    if (rowIdSet && !rowIdSet.has(String(row.id))) return false
    return true
  })

  if (toArchive.length === 0) return 0

  const updated = toArchive.map((row) => ({ ...row, archived: '1' as const }))
  await dexieProductionTableRepo.upsertMany(context, updated)
  return updated.length
}

export const upsertProductionUnitTableRow = async (context: ProductionUnitTableContext, row: BiomassDataRow): Promise<void> => {
  await ensureOfflineReady()
  await dexieProductionTableRepo.upsertRow(context, sanitizeStoredRows([row])[0] ?? row)
}

export const enqueueProductionUnitTableRowUpsert = async (context: ProductionUnitTableContext, row: BiomassDataRow): Promise<void> => {
  if (!isSyncableProductionRow(row)) return

  await enqueueEntityChange({
    entityType: 'productionRecord',
    op: 'upsert',
    entityId: row.id,
    payload: toRecordPayload(context, row)
  })

  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'info',
    title: 'Production record updated',
    detail: 'Sync queued',
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    dedupeKey: `productionRecord:upsert:${context.farmId}:${context.productionUnitId}`,
    incrementCount: 1,
    dedupeWindowMs: 45_000
  }).catch(() => {})
}

export const enqueueProductionUnitTableRowsUpsert = async (
  context: ProductionUnitTableContext,
  rows: BiomassDataRow[],
  options?: {
    syncRowIds?: string[]
    skipAutoSync?: boolean
  }
): Promise<void> => {
  const syncOnly = Array.isArray(options?.syncRowIds) && options.syncRowIds.length > 0
    ? new Set(options.syncRowIds.map(String))
    : null

  const syncable = rows
    .filter(isSyncableProductionRow)
    .filter((row) => (syncOnly ? syncOnly.has(String(row.id)) : true))

  if (syncable.length === 0) return

  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'info',
    title: 'Production records updated',
    detail: `Queued ${syncable.length} row${syncable.length === 1 ? '' : 's'} for sync`,
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    dedupeKey: `productionRecord:upsert:${context.farmId}:${context.productionUnitId}`,
    incrementCount: syncable.length,
    dedupeWindowMs: 45_000
  }).catch(() => {})

  for (const row of syncable) {
    await enqueueEntityChange({
      entityType: 'productionRecord',
      op: 'upsert',
      entityId: row.id,
      payload: toRecordPayload(context, row)
    })
  }

  if (!options?.skipAutoSync) {
    requestDebouncedOutboxSync(50)
  }
}

export const upsertProductionUnitTableRowAndSync = async (
  context: ProductionUnitTableContext,
  row: BiomassDataRow
): Promise<ProductionUnitTableRowMutationResult> => {
  await upsertProductionUnitTableRow(context, row)
  await enqueueProductionUnitTableRowUpsert(context, row)

  if (!isSyncableProductionRow(row)) {
    return {
      data: row,
      syncState: 'synced',
      syncPromise: null
    }
  }

  return buildQueuedEntityMutationResult(row, {
    entityType: 'productionRecord',
    entityId: row.id,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const upsertProductionUnitTableRowsAndSync = async (
  context: ProductionUnitTableContext,
  rows: BiomassDataRow[],
  options?: { syncRowIds?: string[] }
): Promise<void> => {
  const sanitizedRows = sanitizeStoredRows(rows)
  await ensureOfflineReady()
  await dexieProductionTableRepo.upsertMany(context, sanitizedRows)

  await enqueueProductionUnitTableRowsUpsert(context, sanitizedRows, options)
}

export const replaceProductionUnitTableRows = async (context: ProductionUnitTableContext, rows: BiomassDataRow[]): Promise<void> => {
  await ensureOfflineReady()
  await dexieProductionTableRepo.replaceAllForContext(context, sanitizeStoredRows(rows))
}

export const deleteProductionUnitTableRow = async (context: ProductionUnitTableContext, rowId: string): Promise<void> => {
  await ensureOfflineReady()
  await dexieProductionTableRepo.deleteRow(context, rowId)
}

export const readProductionUnitTableRowSyncState = async (rowIds: string[]): Promise<EntityMutationSyncState> => {
  const status = await readProductionUnitTableRowSyncStatus(rowIds)
  return status.syncState
}

export const retryProductionUnitTableRowSync = async (rowIds: string[]): Promise<EntityMutationSyncState> => {
  const status = await retryProductionUnitTableRowSyncStatus(rowIds)
  return status.syncState
}

export const readProductionUnitTableRowSyncStatus = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return readEntityBatchSyncStatus('productionRecord', rowIds)
}

export const retryProductionUnitTableRowSyncStatus = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return retryEntityBatchSyncStatus({
    entityType: 'productionRecord',
    entityIds: rowIds,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const clearProductionUnitTableRowSyncQueueBlockers = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return clearEntityBatchSyncQueueBlockers({
    entityType: 'productionRecord',
    entityIds: rowIds,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const deleteProductionUnitTableRowAndSync = async (
  context: ProductionUnitTableContext,
  rowId: string
): Promise<ProductionUnitTableRowDeleteResult> => {
  await deleteProductionUnitTableRow(context, rowId)
  await enqueueEntityChange({
    entityType: 'productionRecord',
    op: 'delete',
    entityId: rowId,
    payload: {
      farmId: context.farmId,
      productionUnitId: context.productionUnitId,
      rowId
    }
  })

  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'warning',
    title: 'Production record deleted',
    detail: 'Sync queued',
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    dedupeKey: `productionRecord:delete:${rowId}`,
    dedupeWindowMs: 10_000
  }).catch(() => {})

  return buildQueuedEntityMutationResult(undefined, {
    entityType: 'productionRecord',
    entityId: rowId,
    maxEntries: 50,
    maxPasses: 5
  })
}

type ProductionUnitTableSaveOptions = {
  skipSync?: boolean
  syncRowIds?: string[]
}

export const saveProductionUnitTableRows = (
  context: ProductionUnitTableContext,
  rows: BiomassDataRow[],
  options?: ProductionUnitTableSaveOptions
): void => {
  const upgraded = sanitizeStoredRows(rows)
  writeWaterJSON(canonicalKey(context), upgraded)

  const blob = readLegacyBlob()
  const byName = nameKey(context)
  if (byName) blob[byName] = upgraded
  blob[idKey(context)] = upgraded
  writeLegacyBlob(blob)

  if (options?.skipSync) return

  // Fire-and-forget: enqueue each row as an upsert so the API stays in sync.
  void (async () => {
    await enqueueProductionUnitTableRowsUpsert(context, upgraded, options)
  })().catch(() => {})
}

export const updateProductionUnitTableRowEverywhere = (rowId: string, patch: Partial<BiomassDataRow>): void => {
  const blob = readLegacyBlob()
  let changed = false

  const stringPatch: Record<string, string> = {}
  Object.entries(patch).forEach(([key, value]) => {
    if (typeof value === 'string') stringPatch[key] = value
  })

  Object.keys(blob).forEach((key) => {
    const rows = blob[key]
    if (!Array.isArray(rows) || rows.length === 0) return
    const next = rows.map((row) => {
      if (!row || row.id !== rowId) return row
      changed = true
      return { ...row, ...stringPatch }
    })
    blob[key] = next
  })

  if (changed) writeLegacyBlob(blob)
}

export const deleteProductionUnitTableRowEverywhere = (rowId: string): void => {
  const blob = readLegacyBlob()
  let changed = false

  Object.keys(blob).forEach((key) => {
    const rows = blob[key]
    if (!Array.isArray(rows) || rows.length === 0) return
    const next = rows.filter((row) => row?.id !== rowId)
    if (next.length !== rows.length) {
      changed = true
      blob[key] = next
    }
  })

  if (changed) writeLegacyBlob(blob)
}

// Call this when a single production-record row is deleted from the UI so the API is notified.
export const dequeueProductionRow = async (id: string): Promise<void> => {
  await enqueueEntityChange({ entityType: 'productionRecord', op: 'delete', entityId: id })
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void syncEntityOutboxNow({ maxEntries: 10 })
  }
  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'warning',
    title: 'Production record deleted',
    detail: 'Sync queued',
    dedupeKey: `productionRecord:delete:${id}`,
    dedupeWindowMs: 10_000
  }).catch(() => {})
}

export const saveProductionUnitTableBucket = (bucketKey: string, rows: BiomassDataRow[]): void => {
  const key = (bucketKey ?? '').trim()
  if (!key) return
  const blob = readLegacyBlob()
  blob[key] = ensureDailyMortalityField(rows)
  writeLegacyBlob(blob)
}

type ProductionUnitTableRefreshOptions = {
  forceRemote?: boolean
}

export const refreshProductionUnitTableFromApi = async (
  context: ProductionUnitTableContext,
  options?: ProductionUnitTableRefreshOptions
): Promise<BiomassDataRow[]> => {
  const records = await api.productionRecords.list({
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    noCache: options?.forceRemote
  })

  const remoteRows = Array.isArray(records)
    ? records
      // Defensive: if the API returns broader data than requested,
      // never contaminate this context's local rows.
      .filter((record) => String(record.farmId) === String(context.farmId) && String(record.productionUnitId) === String(context.productionUnitId))
      .map(toRowFromRecord)
    : []
  const localRows = await listProductionUnitTableRows(context)

  const pendingOutboxEntries = await getOfflineDb().entityOutbox.toArray()
  const pendingRowOps = new Map(
    pendingOutboxEntries
      .filter((entry) => entry.entityType === 'productionRecord')
      .map((entry) => [entry.entityId, entry.op] as const)
  )
  const pendingDeleteIds = new Set(
    pendingOutboxEntries
      .filter((entry) => entry.entityType === 'productionRecord' && entry.op === 'delete')
      .map((entry) => entry.entityId)
  )

  const remoteById = new Map(remoteRows.map((row) => [row.id, row]))
  const merged: BiomassDataRow[] = localRows.flatMap((row) => {
    const pendingOp = pendingRowOps.get(row.id)
    if (pendingOp === 'upsert') return [row]
    if (pendingOp === 'delete') return []

    const remote = remoteById.get(row.id)
    if (!remote) return []
    return [remote]
  })

  const localIds = new Set(localRows.map((row) => row.id))
  for (const remote of remoteRows) {
    if (pendingDeleteIds.has(remote.id)) continue
    if (localIds.has(remote.id)) continue
    merged.push(remote)
  }

  const sanitized = sanitizeStoredRows(merged)
  await dexieProductionTableRepo.replaceAllForContext(context, sanitized)
  return sanitized
}

export const toProductionRecordPayload = (context: ProductionUnitTableContext, row: BiomassDataRow): Record<string, unknown> =>
  toRecordPayload(context, row)
